/**
 * SearchTypeahead — debounced ID/name search over the Thrive searches
 * picker (`GET /api/web/v2/searches?q=`).
 *
 * Debounce pattern reused 1:1 from `ImportsPage.tsx`'s `searchText`/`q`
 * `setTimeout` debounce (`SEARCH_DEBOUNCE_MS = 300`). Every non-empty query
 * reaches the wire — see `MIN_QUERY_LENGTH`. Only a query that is empty after
 * trimming shows the initial (unfiltered: Open + Business Development, plus
 * the TEST whitelist) view,
 * and in that case the list genuinely IS unfiltered, which is what it looks
 * like.
 *
 * NOT modeled on `SequencePicker.tsx` — that component is a pure
 * client-side `useMemo` filter over an already-fully-loaded list, which
 * doesn't apply here (Thrive has ~4,353 searches; this component always
 * round-trips to the server, even for the initial/empty-query view). Its
 * search-icon + input visual is a reasonable look-and-feel reference only.
 *
 * One mount opts into more: the New Import wizard's Thrive-source picker passes
 * `statuses` and gets a status pill per row (epic
 * 2026-10-15-thrive-search-import, sub-task 03). With more than one status it
 * also gets a status control above the input; since Jared's feedback of
 * 2026-10-20 the picker passes `all` alone, so there is no control. Every other
 * mount passes nothing and reads Open + Business Development, as before.
 *
 * Tokenized: token color utilities + `text-*` roles only, no inline
 * `style={{}}`, no arbitrary `text-[Npx]`.
 */

import { ContextBadge, Spinner } from "@nu-assist/ui";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import type { SearchStatusFilter } from "../../../../clients/imports.client";
import { searchStatusClasses } from "../../../../components/searches/searchStatus";

const SEARCH_DEBOUNCE_MS = 300;
// 1, not 2. At 2 the effect below promoted a 1-char query to `q = ""` — the
// UNFILTERED view — so typing `4` showed the first page of all 138 searches
// with `#5091 GM Healthcare` among them, reading as a filter that had matched
// rather than a filter that had never run. A gate that silently widens is
// worse than no gate. Searches are indexed on both matched columns and the
// endpoint reads a local mirror, so a 1-char query is a cheap prefix lookup.
const MIN_QUERY_LENGTH = 1;
// Anti-flicker: only show the spinner once a fetch has been in flight this
// long. A cache hit resolves well under this, so the spinner stays invisible
// for the common case and only appears for a genuinely slow lookup.
const SPINNER_DELAY_MS = 700;

export interface SearchTypeaheadOption {
  id: string;
  name: string;
  /** Thrive's own `job_title` attribute, separate from `name` — rendered as
   * its own column (id · job_title · company) instead of folded into one
   * string. `null`/absent for the rare record missing it (falls back to
   * showing `name` alone). */
  jobTitle?: string | null;
  /** Thrive's `client_company.name`, separate from `name` — see `jobTitle`. */
  company?: string | null;
  isTest?: boolean;
  /** Thrive's status, verbatim. Shown as a pill only by a picker that passes
   *  `statuses`; the Open + BD pickers leave it off, as before. */
  status?: string | null;
  /** The summary fields the Thrive-source picker shows once a search is
   *  picked (sub-task 01 of epic 2026-10-15-thrive-search-import). */
  candidaciesCount?: number | null;
  thriveCreatedAt?: string | null;
  closedAt?: string | null;
}

/** The status control's labels, in the order `statuses` lists them. */
const STATUS_FILTER_LABELS: Record<SearchStatusFilter, string> = {
  closed: "Closed",
  open: "Open",
  bd: "BD",
  lost: "Lost",
  all: "All",
};

export interface SearchTypeaheadResult {
  items: SearchTypeaheadOption[];
  /** The REAL total match count (never an estimate) — drives "Showing N of X". */
  total: number;
}

export interface SearchTypeaheadProps {
  /** Selected search id, or `null` when nothing is linked yet. */
  value: string | null;
  /** Selected search's name (paired with `value`) — shown in the "selected" summary. */
  valueName: string | null;
  onChange: (searchId: string | null, searchName: string | null) => void;
  /** Called with the whole picked option, just before `onChange`, for a host
   *  that shows more of it than the name (the Thrive-source summary line). */
  onPick?: (option: SearchTypeaheadOption) => void;
  /** Returns the current page of options for `query` (`""` = initial view).
   *  Called with the active status filter only when `statuses` is set. */
  search: (query: string, status?: SearchStatusFilter) => Promise<SearchTypeaheadResult>;
  /**
   * Opt-in status filter, for the Thrive-source picker of the New Import
   * wizard: `search` is called with the FIRST status on mount and every row
   * carries a status pill. With more than one status, a segmented control
   * above the input switches between them in this order. Absent (every other
   * mount) = no control, no pill, and `search` is called with the query alone,
   * exactly as before.
   */
  statuses?: readonly SearchStatusFilter[];
  /** Focus the input on mount — the wizard step whose whole job is this picker. */
  autoFocus?: boolean;
  /**
   * Which surface the control sits ON, so the input can be the tier BELOW it.
   *
   * Not a decoration: the two hosts have different backgrounds and the input has
   * to read as recessed in both. The New Import form sits on `--bg`, so its
   * input is `--bg` with a border (`"bg"`, the default). The finder's controls
   * are all `--surface` — filename input, Cancel, tag chips, owner menu, rows —
   * so a `--bg` input there was a black box beside five grey ones. Passing
   * `"surface"` makes it match its neighbours instead of its origin.
   */
  surface?: "bg" | "surface";
  /**
   * When true, "Change" opens the picker WITHOUT clearing the selection: the
   * host keeps `value` until a new search is picked, and a "Keep <current>"
   * button (or Escape) closes the picker with no `onChange` at all. Combine &
   * Filter needs this — clearing its target re-keys the preview, so every
   * Change sent a target-less (full merge) preview and flipped each list to
   * "no target" while the recruiter was still choosing (PR #993 E2E S5).
   * Default `false`: Change calls `onChange(null, null)` as it always has.
   */
  keepValueWhileChanging?: boolean;
  /**
   * Focusing or clicking the EMPTY input opens the panel on the initial view
   * (the first page for the active status, in the API's order), so a recruiter
   * can browse without typing; a status change refreshes that list in place.
   * The New Import wizard's two pickers pass it (Jose, local testing,
   * 2026-10-20). Default `false`: the panel opens on typing only, as before.
   * Costs no request: the initial view is fetched on mount either way.
   */
  openOnFocus?: boolean;
}

/** `1,015 candidacies` / `1 candidacy`. */
function candidaciesLabel(count: number): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "candidacy" : "candidacies"}`;
}

function optionLabel(opt: { id: string; name: string }): string {
  return `${opt.id} - ${opt.name}`;
}

/**
 * Split `text` around case-insensitive occurrences of `query` and wrap each
 * match in a highlighted `<mark>`. Plain string split + escape (no
 * `dangerouslySetInnerHTML`, no unescaped `query` fed into `RegExp`).
 * Returns `text` unchanged (as a single string, not an array) when `query`
 * is empty or doesn't occur in `text` — avoids wrapping every option in a
 * no-op `<span>` for the common no-match case. A 1-char query DOES highlight:
 * the gate here tracks `MIN_QUERY_LENGTH` so what is marked is always exactly
 * what was sent to the server.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  const trimmed = query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return text;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length === 1) return text;
  // Composite key (index + content), not a bare index — `parts` is rebuilt
  // fresh from `text`/`query` every render, never reordered/filtered in
  // place, so this is purely to satisfy noArrayIndexKey, not a real
  // reconciliation concern.
  return parts.map((part, i) =>
    part.toLowerCase() === trimmed.toLowerCase() ? (
      <mark key={`${i}-${part}`} className="rounded-sm bg-mark text-mark-ink">
        {part}
      </mark>
    ) : (
      <span key={`${i}-${part}`}>{part}</span>
    )
  );
}

export function SearchTypeahead({
  value,
  valueName,
  onChange,
  search,
  surface = "bg",
  keepValueWhileChanging = false,
  statuses,
  autoFocus = false,
  onPick,
  openOnFocus = false,
}: SearchTypeaheadProps) {
  const [status, setStatus] = useState<SearchStatusFilter | undefined>(statuses?.[0]);
  // Tailwind cannot see a class assembled at runtime, so these are whole
  // literals rather than `bg-${surface}` — the interpolated form compiles to no
  // CSS at all and the input renders transparent.
  const inputBg = surface === "surface" ? "bg-surface" : "bg-bg";
  const panelBg = surface === "surface" ? "bg-surface-raised" : "bg-surface";
  // Per-instance, not a module-scope literal. This used to be a stable
  // `"link-search-listbox"` on the premise that the component is rendered at
  // most once per screen, with a note to revisit if two ever shared a page.
  // The imports toolbar's "filter by search" control is that second mount
  // site — the finder already renders one — and duplicate ids make the second
  // panel unreachable by `getElementById`, so both inputs' `aria-controls` and
  // `aria-activedescendant` would resolve to the FIRST panel's rows.
  const listboxId = useId();
  const [searchText, setSearchText] = useState(""); // raw input
  const [q, setQ] = useState(""); // debounced + 2-char-gated → wire
  // Panel opened by focus/click on an empty input (`openOnFocus` only). Stored,
  // unlike `open`: focus is not readable from anything else on screen.
  const [browsing, setBrowsing] = useState(false);
  // Picker open over a kept selection (`keepValueWhileChanging` only).
  const [editing, setEditing] = useState(false);
  const editingKept = keepValueWhileChanging && editing && value != null;

  const changeButtonRef = useRef<HTMLButtonElement>(null);
  const refocusChange = useRef(false);

  function closeEditing() {
    setEditing(false);
    setSearchText("");
    setQ("");
  }

  // Keep / Escape: the control the recruiter was using unmounts, so focus
  // goes back to the Change button rather than falling to <body>.
  function cancelEditing() {
    refocusChange.current = true;
    closeEditing();
  }

  // With `keepValueWhileChanging`, a pick closes the picker (the new value is
  // what is shown), and so does a `value` changed from outside — a "Start
  // over" that does not remount this component must not leave it open
  // (/pr-review iter 4, Agent A N8 / C4-3). Without it, both behave as they
  // always have: a pick only calls `onChange`, and the typed text stays, so
  // Change reopens the picker on it (/pr-review iter 5, Agent A N12).
  function pick(opt: SearchTypeaheadOption) {
    setBrowsing(false);
    if (keepValueWhileChanging) closeEditing();
    onPick?.(opt);
    onChange(opt.id, opt.name);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is the trigger, not an input
  useEffect(() => {
    setBrowsing(false);
    if (keepValueWhileChanging) closeEditing();
    else setEditing(false);
  }, [value]);

  useEffect(() => {
    if (!editing && refocusChange.current) {
      refocusChange.current = false;
      changeButtonRef.current?.focus();
    }
  }, [editing]);

  // Debounce `searchText` into `q`. With `MIN_QUERY_LENGTH = 1` the only text
  // that resolves to "" is text that is empty after trimming, so the list on
  // screen always corresponds to what the user typed.
  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchText.trim();
      setQ(trimmed.length >= MIN_QUERY_LENGTH ? trimmed : "");
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchText]);

  // Fetches whenever the effective query changes (incl. the initial "" fetch
  // on mount — the initial view IS a real backend call, just not a NEW one
  // triggered by a 1-char keystroke). `placeholderData: (prev) => prev` keeps
  // the previous query's results on screen while a refined query is in
  // flight (same "stale results stay visible" behavior the old manual state
  // had, since `result` was never cleared between fetches). `retry: false` —
  // an error should surface immediately via `isError` rather than be masked
  // behind TanStack Query's default 3-retry backoff. The endpoint is a local
  // Postgres read now (it calls nothing upstream), so anything reaching
  // `isError` is a network or gateway failure, and retrying three times only
  // delays telling the user.
  const {
    data: result = { items: [], total: 0 },
    isFetching,
    isError,
  } = useQuery({
    // The status joins the key only for the picker that has one, so every
    // other mount keeps the cache entries it always had.
    queryKey: statuses ? ["searches", q, status] : ["searches", q],
    queryFn: () => (statuses ? search(q, status) : search(q)),
    placeholderData: (prev) => prev,
    retry: false,
  });

  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (!isFetching) {
      setShowSpinner(false);
      return;
    }
    const id = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
    return () => clearTimeout(id);
  }, [isFetching]);

  // Derived, never stored — see the panel's comment below. Keyed on the RAW
  // input so the panel opens on the first keystroke rather than after the
  // debounce. `browsing` adds the focus-opened view (`openOnFocus` only).
  const typed = searchText.trim().length > 0;
  const open = typed || browsing;

  // TRUE while the panel is open because someone typed, but the results on hand
  // are still the mount-time UNFILTERED view. Three correct lines conspired to
  // produce the very defect this component was changed to remove:
  //
  //   * `open` follows the raw text, so the panel appears on keystroke 1;
  //   * `placeholderData: (prev) => prev` supplies the previous result, and on
  //     the first keystroke `prev` IS the `q=""` list;
  //   * `SPINNER_DELAY_MS` (700) > `SEARCH_DEBOUNCE_MS` (300), so nothing says
  //     "loading" during the wait.
  //
  // Measured by the round-2 review: typing `zzzzz` showed ten unrelated searches
  // under the sentence "Showing 10 of 138" for ~350ms, with no spinner and no
  // highlight, presented as the answer to `zzzzz`. Worse than the always-open
  // panel it replaced — there the unfiltered list was furniture, here it appears
  // BECAUSE the recruiter typed, which frames it as the result.
  //
  // Narrow on purpose: only the empty-`q` placeholder is suppressed. Refining
  // `44` → `441` keeps showing the `44` rows, which is a genuine subset and the
  // reason `placeholderData` is here — suppressing that too would reintroduce
  // the flicker it exists to prevent.
  //
  // Only for TYPED text: a panel opened by focus on an empty input asked for
  // the initial view, so showing it is the answer, not a placeholder.
  const showingUnfilteredForAQuery = typed && q === "";

  // Keyboard-active option. STORED (unlike `open`) because it is genuinely its
  // own state: nothing else on screen implies it, and it has to survive
  // re-renders caused by the fetch resolving underneath it.
  //
  // `-1` means "nothing active", which is the resting state after every query
  // change: reusing the previous index would leave the highlight on whatever
  // row happens to be in that position in the NEW result set, so the first
  // ArrowDown after refining a query would confirm a row the recruiter never
  // looked at. Enter with nothing active is a no-op for the same reason.
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Keyed on `q` + the row COUNT, not on `result.items`. The array identity is
  // the tempting dep and it is wrong: `result` falls back to a fresh
  // `{ items: [], total: 0 }` literal on every render before data arrives, so
  // `[result.items]` re-ran this effect continuously — biome's
  // `useExhaustiveDependencies` caught it. `q` covers the refine case (the one
  // that matters: the highlight must not survive onto a different result set)
  // and the count covers a same-query refetch that changed size.
  // The deps are TRIGGERS, not values read inside the effect — the body only
  // resets state, so biome sees them as unnecessary. Removing them would run the
  // reset once on mount and never again, leaving the highlight pointing into a
  // stale result set after every refine.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    setActiveIndex(-1);
  }, [q, status, result.items.length]);

  // Keep the active row inside the scroll box. `block: "nearest"` scrolls only
  // when it has to, so arrowing through visible rows does not jump the panel.
  useEffect(() => {
    if (activeIndex >= 0) optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function moveActive(delta: number) {
    const count = result.items.length;
    if (count === 0) return;
    setActiveIndex((prev) => {
      // From "nothing active", Down goes to the first row and Up to the last —
      // the standard combobox behaviour, and what makes a single ArrowUp a
      // usable way to reach the end of a short list.
      if (prev < 0) return delta > 0 ? 0 : count - 1;
      // Wrap, rather than clamp: clamping at the ends makes a held arrow key
      // feel stuck, and there is no scroll position to preserve here.
      return (prev + delta + count) % count;
    });
  }

  const showingLabel = useMemo(() => {
    if (result.total <= result.items.length) return null;
    return `Showing ${result.items.length} of ${result.total}`;
  }, [result]);

  if (value && !editingKept) {
    return (
      <div
        data-testid="link-search-selected"
        className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2"
      >
        <span className="text-body text-text">
          {optionLabel({ id: value, name: valueName ?? "" })}
        </span>
        <button
          type="button"
          data-testid="link-search-change"
          ref={changeButtonRef}
          onClick={() => (keepValueWhileChanging ? setEditing(true) : onChange(null, null))}
          className="text-caption text-accent hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-2">
      {/* Above the input, not inside the results panel: the panel is a
          `listbox`, which may hold options only, and it opens on typing, so a
          control inside it would hide which statuses the next query reads. */}
      {statuses && statuses.length > 1 ? (
        <fieldset data-testid="search-status-filter" className="flex flex-wrap items-center gap-1">
          <legend className="sr-only">Search status</legend>
          {statuses.map((value) => (
            <button
              key={value}
              type="button"
              data-testid={`search-status-option-${value}`}
              aria-pressed={status === value}
              // A mouse click keeps focus in the input, so a panel opened by
              // focus stays open and shows the new status's list.
              onMouseDown={(e) => {
                if (openOnFocus) e.preventDefault();
              }}
              onClick={() => setStatus(value)}
              className={`rounded-md px-2 py-0.5 text-caption ${
                status === value
                  ? "bg-accent-muted text-accent"
                  : "text-text-secondary hover:text-text"
              }`}
            >
              {STATUS_FILTER_LABELS[value]}
            </button>
          ))}
        </fieldset>
      ) : null}
      <div className="relative">
        <Search
          size={13}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
          aria-hidden
        />
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => {
            if (openOnFocus) setBrowsing(true);
          }}
          // A click on an already-focused input (after Escape closed the panel)
          // fires no focus event, so it opens the panel itself.
          onClick={() => {
            if (openOnFocus) setBrowsing(true);
          }}
          onBlur={() => setBrowsing(false)}
          // Only when opened by "Change" over a kept selection — the recruiter
          // asked to type, so the caret goes where the typing happens.
          // biome-ignore lint/a11y/noAutofocus: focus follows the Change click, or a host step that is only this picker
          autoFocus={autoFocus || editingKept}
          onKeyDown={(e) => {
            // Escape over a kept selection cancels the change outright.
            if (e.key === "Escape" && editingKept) {
              e.preventDefault();
              cancelEditing();
              return;
            }
            // Escape clears rather than just hiding: `open` is derived from the
            // text, so a hide-without-clear would need a second piece of state
            // that could disagree with it. Clearing collapses the panel AND
            // returns the control to its resting look, which is what a user
            // pressing Escape in a typeahead means.
            if (e.key === "Escape" && searchText) {
              e.preventDefault();
              setSearchText("");
              setBrowsing(false);
              return;
            }
            if (e.key === "Escape" && browsing) {
              e.preventDefault();
              setBrowsing(false);
              return;
            }
            // Down on a closed, focus-opening picker opens it, as in a <select>.
            if (!open && openOnFocus && e.key === "ArrowDown") {
              e.preventDefault();
              setBrowsing(true);
              return;
            }
            if (!open) return;
            // `preventDefault` on the arrows stops the caret jumping to the ends
            // of the text while the list is being browsed — without it, Down
            // moved the highlight AND the cursor, and the next keystroke landed
            // in the wrong place.
            if (e.key === "ArrowDown") {
              e.preventDefault();
              moveActive(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              moveActive(-1);
            } else if (e.key === "Home") {
              e.preventDefault();
              if (result.items.length) setActiveIndex(0);
            } else if (e.key === "End") {
              e.preventDefault();
              if (result.items.length) setActiveIndex(result.items.length - 1);
            } else if (e.key === "Enter") {
              const opt = activeIndex >= 0 ? result.items[activeIndex] : undefined;
              // Only swallow Enter when it actually selects something.
              // Left to bubble otherwise — deliberately, but NOT because a
              // wrapping form needs it: no host has one (BctPage's form closes
              // before the selection bar; AiReviewPage and LinkSearchStep have
              // none). The reason is that swallowing a key this component did
              // not act on is a decision to break whatever a future host does
              // with it, made on that host's behalf and invisible from there.
              if (opt) {
                e.preventDefault();
                pick(opt);
              }
            }
          }}
          placeholder="Search by name or ID…"
          aria-label="Search Thrive searches"
          role="combobox"
          aria-expanded={open}
          // Conditional, not unconditional: the listbox only exists while the
          // panel is open, so in the resting state this pointed at a
          // nonexistent id — `aria-valid-attr-value` in axe, and a dangling
          // reference tells a screen reader nothing about a control it cannot
          // find.
          aria-controls={open ? listboxId : undefined}
          aria-autocomplete="list"
          // Points at the ACTIVE option so a screen reader announces the row the
          // arrows moved to. Focus itself never leaves the input — that is the
          // combobox pattern, and it is what keeps typing possible mid-browse.
          aria-activedescendant={
            activeIndex >= 0 && result.items[activeIndex]
              ? `${listboxId}-opt-${result.items[activeIndex].id}`
              : undefined
          }
          data-testid="link-search-input"
          className={`w-full rounded-md border border-border ${inputBg} py-1.5 pl-8 pr-3 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none`}
        />
      </div>
      {editingKept ? (
        <button
          type="button"
          data-testid="link-search-keep"
          onClick={cancelEditing}
          className="self-start text-caption text-accent hover:underline"
        >
          Keep {optionLabel({ id: value, name: valueName ?? "" })}
        </button>
      ) : null}
      {/* CLOSED until something is typed (or, with `openOnFocus`, until the
       * input is focused), and FLOATING when open.
       *
       * Both properties are corrections of the same defect: the panel used to
       * render inline and unconditionally, so the initial (unfiltered) view was
       * always on screen and its height pushed the sibling controls out of the
       * row — in the finder it displaced the filename input and wrapped the
       * scope menu onto another line.
       *
       * `open` is derived from the RAW input text, not from the debounced `q`:
       * keying it on `q` would leave the panel shut for the 300ms after the
       * first keystroke and then pop it open, so the control would feel like it
       * ignored the first character. Derived rather than stored so it cannot
       * disagree with what is in the box.
       *
       * The fetch is NOT gated on `open` — the mount-time `q=""` request still
       * runs, which is what keeps `total` available for the footer the moment
       * the panel appears. Cheap (a local mirror read) and deliberate.
       */}
      {open ? (
        <div
          id={listboxId}
          // biome-ignore lint/a11y/useSemanticElements: the semantic element for
          // `listbox` is <select>, which cannot hold the id + job-title +
          // company columns or the TEST badge these options render.
          role="listbox"
          // NOT focusable by a user, and that is the combobox pattern: focus
          // stays on the input so typing continues mid-browse, and the active
          // row is announced via `aria-activedescendant`. `tabIndex={-1}`
          // satisfies `useFocusableInteractive` (the element IS programmatically
          // focusable) without putting it in the tab order, where it would add a
          // stop that does nothing.
          tabIndex={-1}
          // Pressing a row must not blur the input first: the blur closes a
          // focus-opened panel, which would unmount the row before its click.
          onMouseDown={(e) => e.preventDefault()}
          aria-label="Thrive search results"
          data-testid="link-search-results"
          className={`absolute left-0 right-0 top-full z-20 mt-1 flex max-h-56 flex-col gap-1 overflow-y-auto rounded-md border border-border ${panelBg} p-1 shadow-lg`}
        >
          {isError ? (
            // Distinct from "No searches match." — a failed request is not the
            // same as a genuine 0-match result, and takes priority over both the
            // spinner and any stale results. (The request failing is now the
            // only way to get here: the API reads its local mirror, so it has no
            // upstream of its own to be unreachable.)
            <p data-testid="link-search-error" className="px-1 py-2 text-caption text-warning">
              Couldn’t load searches — try again
            </p>
          ) : showingUnfilteredForAQuery ? (
            // The recruiter typed, and the only rows on hand are the unfiltered
            // view. Show that a search is underway INSTEAD of those rows — a
            // "Searching…" line cannot be mistaken for a result, and ten unrelated
            // searches under "Showing 10 of 138" can. Not gated on
            // `SPINNER_DELAY_MS`: that delay exists to keep the spinner out of the
            // way of a fast REFINEMENT, and here there is nothing else to show, so
            // waiting 700ms would just leave the panel blank-then-wrong.
            <div
              data-testid="link-search-searching"
              className="flex items-center gap-2 px-1 py-2 text-caption text-text-secondary"
            >
              <span className="text-accent">
                <Spinner size="sm" label="Searching" />
              </span>
              <span aria-hidden="true">Searching…</span>
            </div>
          ) : (
            <>
              {showSpinner ? (
                <div
                  data-testid="link-search-loading"
                  className="flex items-center gap-2 px-1 py-2 text-caption text-text-secondary"
                >
                  <span className="text-accent">
                    <Spinner size="sm" label="Searching" />
                  </span>
                  {/* Decorative — the Spinner's own aria-label already announces
                    "Searching" via its live region; this text is for sighted
                    users only. */}
                  <span aria-hidden="true">Searching…</span>
                </div>
              ) : null}
              {result.items.length === 0 && !isFetching ? (
                // One empty state, because there is now only one source. The
                // backend reads its Postgres mirror and nothing else, so "no
                // matches" is the whole answer — there is no unchecked window to
                // qualify. (This used to be two states: the API ran a live Thrive
                // lookup on a mirror miss and flagged `thrive_unreachable` when
                // that call failed. Both the lookup and the flag are gone.)
                <p
                  data-testid="link-search-none"
                  className="px-1 py-2 text-caption text-text-muted"
                >
                  No searches match.
                </p>
              ) : (
                result.items.map((opt, i) => (
                  <button
                    key={opt.id}
                    // `role="option"` on a real <button>: valid, and it keeps the
                    // native click/Enter behaviour the mouse path relies on. Safe
                    // here where it would NOT be on the finder's rows — `option`
                    // is children-presentational, so it may flatten its subtree,
                    // and these options contain only text plus a badge. The
                    // finder's rows carry real links, which is why they are a
                    // plain wrapper with an inner button instead.
                    // biome-ignore lint/a11y/useSemanticElements: <option> renders
                    // text only — no columns, no badge — and cannot live outside a
                    // <select>.
                    role="option"
                    aria-selected={i === activeIndex}
                    id={`${listboxId}-opt-${opt.id}`}
                    ref={(el) => {
                      optionRefs.current[i] = el;
                    }}
                    type="button"
                    data-testid={`link-search-option-${opt.id}`}
                    data-search-id={opt.id}
                    onClick={() => pick(opt)}
                    // Pointer and keyboard share ONE highlight: hovering moves the
                    // active index rather than painting a second, competing
                    // highlight, so Enter always confirms the row that looks
                    // chosen no matter which device put it there.
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left ${
                      i === activeIndex ? "bg-accent-muted" : ""
                    }`}
                  >
                    {/* Clearly separated columns (id · job_title · company) instead
                      of one flat "<id> - <name>" string — job_title/company are
                      the fields recruiters actually scan for; falls back to the
                      single `name` label when Thrive doesn't have them split out. */}
                    <span className="flex min-w-0 flex-1 items-baseline gap-2 overflow-hidden">
                      <span
                        data-testid={`link-search-option-id-${opt.id}`}
                        className="shrink-0 font-mono text-caption text-text-secondary"
                      >
                        #{highlightMatch(opt.id, q)}
                      </span>
                      {opt.jobTitle || opt.company ? (
                        <span className="flex min-w-0 flex-col overflow-hidden">
                          <span className="truncate text-body text-text">
                            {opt.jobTitle ? highlightMatch(opt.jobTitle, q) : opt.name}
                          </span>
                          {opt.company ? (
                            <span className="truncate text-caption text-text-muted">
                              {highlightMatch(opt.company, q)}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="truncate text-body text-text">
                          {highlightMatch(opt.name, q)}
                        </span>
                      )}
                    </span>
                    {opt.candidaciesCount != null ? (
                      // Muted, before the pill: helps tell same-titled searches
                      // apart. Shown wherever the host maps the field (both New
                      // Import pickers), from the mirror's hourly count.
                      <span
                        data-testid={`link-search-option-count-${opt.id}`}
                        className="shrink-0 text-caption text-text-muted"
                      >
                        {candidaciesLabel(opt.candidaciesCount)}
                      </span>
                    ) : null}
                    {statuses && opt.status ? (
                      <span
                        data-testid={`link-search-option-status-${opt.id}`}
                        className={`shrink-0 ${searchStatusClasses(opt.status)}`}
                      >
                        {opt.status}
                      </span>
                    ) : null}
                    {opt.isTest ? (
                      <ContextBadge
                        data-testid={`link-search-test-badge-${opt.id}`}
                        className="shrink-0 bg-warning-muted text-warning-ink"
                      >
                        TEST
                      </ContextBadge>
                    ) : null}
                  </button>
                ))
              )}
            </>
          )}
          {/* INSIDE the floating panel, not below it. As a sibling it stayed on
              screen with the panel shut, leaving a bare "Showing 7 of 9" under
              an empty-looking input with nothing to relate it to.

              Suppressed during an error — placeholderData keeps the PRIOR
              query's `result.total` around while `isError` is true, so without
              this gate the footer would keep citing stale counts underneath the
              "Couldn't load searches" message.
              /pr-review iter 1 on PR #498, Agent A + Agent C (converging). */}
          {!isError && !showingUnfilteredForAQuery && showingLabel ? (
            <p
              data-testid="link-search-showing"
              className="px-1 pt-1 text-caption text-text-secondary"
            >
              {showingLabel}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
